/**
 * PublicRecordsTable Component
 * 
 * Structured table for viewing and managing public records (bankruptcies, etc.)
 * from credit reports. Supports single and bulk dispute actions.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  FileSearch,
  AlertTriangle,
  Plus,
  CheckCircle,
  Filter,
  Gavel,
  X,
  Eye,
  Calendar,
  Building,
  Hash,
  Scale,
  AlertCircle,
  Info
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { cn } from '../../lib/utils';

/**
 * Bureau Badge Component
 */
const BureauBadge = ({ bureau }) => {
  const getBureauDisplay = (code) => {
    if (!code) return null;
    const normalized = code.toUpperCase();
    if (normalized === 'EX' || normalized.includes('EXPERIAN')) return { name: 'Experian', color: 'bg-emerald-100 text-emerald-700' };
    if (normalized === 'TU' || normalized.includes('TRANSUNION')) return { name: 'TransUnion', color: 'bg-amber-100 text-amber-700' };
    if (normalized === 'EQ' || normalized.includes('EQUIFAX')) return { name: 'Equifax', color: 'bg-red-100 text-red-700' };
    return { name: code, color: 'bg-slate-100 text-slate-600' };
  };
  
  const display = getBureauDisplay(bureau);
  if (!display) return <span className="text-slate-400 text-sm">--</span>;
  
  return (
    <span className={cn('text-xs px-2 py-1 rounded-full font-medium', display.color)}>
      {display.name}
    </span>
  );
};

/**
 * Status Badge Component
 */
const StatusBadge = ({ status }) => {
  if (!status) return <span className="text-slate-400 text-sm">--</span>;
  
  const normalized = status.toLowerCase();
  let color = 'bg-slate-100 text-slate-600';
  
  if (normalized.includes('discharged') || normalized.includes('dismissed')) {
    color = 'bg-emerald-100 text-emerald-700';
  } else if (normalized.includes('active') || normalized.includes('filed') || normalized.includes('pending')) {
    color = 'bg-red-100 text-red-700';
  }
  
  return (
    <span className={cn('text-xs px-2 py-1 rounded-full font-medium capitalize', color)}>
      {status}
    </span>
  );
};

/**
 * Empty State Component for Public Records - Themed
 */
const PublicRecordsEmptyState = ({ theme = {} }) => (
  <div className="text-center py-12 px-6">
    <div 
      className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
      style={{ 
        background: theme?.positiveBox?.bg || 'rgba(15,172,108,0.10)',
        border: `1px solid ${theme?.positiveBox?.border || 'rgba(15,172,108,0.16)'}`,
      }}
    >
      <CheckCircle size={32} style={{ color: theme?.positiveBox?.color || '#0B8C58' }} />
    </div>
    <h3 
      className="text-lg font-bold mb-2"
      style={{ color: theme?.text?.primary || '#111827' }}
    >
      No Public Records Found
    </h3>
    <p 
      className="text-sm max-w-md mx-auto mb-4"
      style={{ color: theme?.text?.muted || '#6B7280' }}
    >
      No bankruptcy-related public records were found in your current credit report.
    </p>
    <div 
      className="p-4 rounded-xl max-w-lg mx-auto"
      style={{ 
        background: theme?.positiveBox?.bg || 'rgba(15,172,108,0.10)',
        border: `1px solid ${theme?.positiveBox?.border || 'rgba(15,172,108,0.16)'}`,
      }}
    >
      <div className="flex items-start gap-3">
        <Info size={18} className="mt-0.5 flex-shrink-0" style={{ color: theme?.positiveBox?.color || '#0B8C58' }} />
        <p 
          className="text-sm text-left"
          style={{ color: theme?.positiveBox?.color || '#0B8C58' }}
        >
          This is generally a positive sign. If a bankruptcy-related public record is 
          detected later, it will appear here organized by bureau.
        </p>
      </div>
    </div>
  </div>
);

/**
 * Bulk Action Bar Component
 */
const BulkActionBar = ({ selectedCount, onDispute, onClear }) => {
  if (selectedCount === 0) return null;
  
  return (
    <div className="sticky bottom-0 left-0 right-0 p-4 bg-stone-800 rounded-xl shadow-lg border border-slate-700 flex items-center justify-between animate-in slide-in-from-bottom-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
          <CheckCircle size={20} className="text-white" />
        </div>
        <div>
          <p className="text-white font-medium">{selectedCount} record{selectedCount === 1 ? '' : 's'} selected</p>
          <p className="text-slate-400 text-sm">Ready for bulk action</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          onClick={onClear}
          className="text-slate-300 hover:text-white hover:bg-slate-700"
          data-testid="clear-selection"
        >
          <X size={16} className="mr-1" />
          Clear
        </Button>
        <Button
          onClick={onDispute}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="bulk-dispute-records-btn"
        >
          <Gavel size={16} className="mr-2" />
          Dispute Selected
        </Button>
      </div>
    </div>
  );
};

/**
 * Record Detail Modal
 */
const RecordDetailModal = ({ record, open, onClose }) => {
  if (!record) return null;
  
  const formatValue = (val) => {
    if (val === null || val === undefined || val === '') return '--';
    if (typeof val === 'number') return val.toLocaleString();
    return val;
  };
  
  const detailFields = [
    { label: 'Record Type', value: record.type || record.record_type, icon: FileSearch },
    { label: 'Filing Date', value: record.filing_date || record.date, icon: Calendar },
    { label: 'Status', value: record.status, icon: Scale },
    { label: 'Court', value: record.court, icon: Building },
    { label: 'Case/Reference Number', value: record.reference_number || record.case_number, icon: Hash },
    { label: 'Amount', value: record.amount ? `$${record.amount.toLocaleString()}` : null, icon: Scale },
    { label: 'Liabilities', value: record.liabilities ? `$${record.liabilities.toLocaleString()}` : null, icon: AlertTriangle },
    { label: 'Assets', value: record.assets ? `$${record.assets.toLocaleString()}` : null, icon: CheckCircle },
    { label: 'Bureau', value: record.bureau_code || record.bureau, icon: FileSearch },
  ];
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle size={20} className="text-red-500" />
            Public Record Details
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* Type Header */}
          <div className="p-4 bg-red-50 border border-red-100 rounded-xl">
            <p className="text-lg font-semibold text-red-800 capitalize">
              {(record.type || record.record_type || 'Public Record')?.replace(/_/g, ' ')}
            </p>
            {record.status && (
              <p className="text-sm text-red-600 mt-1">Status: {record.status}</p>
            )}
          </div>
          
          {/* Detail Grid */}
          <div className="grid grid-cols-2 gap-3">
            {detailFields.filter(f => f.value).map((field, idx) => {
              const Icon = field.icon;
              return (
                <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                    <Icon size={12} />
                    <span>{field.label}</span>
                  </div>
                  <p className="text-sm font-medium text-stone-800">{formatValue(field.value)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Main PublicRecordsTable Component
 */
export const PublicRecordsTable = ({ publicRecords = [], bureauCounts = {}, theme = {} }) => {
  const navigate = useNavigate();
  
  // State
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [bureauFilter, setBureauFilter] = useState('all');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Enrich records with IDs
  const enrichedRecords = useMemo(() => {
    return publicRecords.map((record, idx) => ({
      ...record,
      record_id: record.record_id || `pr-${idx}`,
    }));
  }, [publicRecords]);
  
  // Compute actual bureau counts from records data
  const actualBureauCounts = useMemo(() => {
    const counts = { transunion: 0, experian: 0, equifax: 0, unknown: 0 };
    enrichedRecords.forEach(record => {
      const bureau = (record.bureau_code || record.bureau || '').toUpperCase();
      if (bureau === 'TU' || bureau.includes('TRANSUNION')) counts.transunion++;
      else if (bureau === 'EX' || bureau.includes('EXPERIAN')) counts.experian++;
      else if (bureau === 'EQ' || bureau.includes('EQUIFAX')) counts.equifax++;
      else counts.unknown++;
    });
    return counts;
  }, [enrichedRecords]);
  
  // Total records
  const totalRecords = enrichedRecords.length;
  
  // Filter records
  const filteredRecords = useMemo(() => {
    let filtered = enrichedRecords;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(record => 
        (record.type || record.record_type || '').toLowerCase().includes(query) ||
        (record.court || '').toLowerCase().includes(query) ||
        (record.reference_number || record.case_number || '').toLowerCase().includes(query) ||
        (record.status || '').toLowerCase().includes(query)
      );
    }
    
    // Bureau filter
    if (bureauFilter !== 'all') {
      filtered = filtered.filter(record => {
        const bureau = (record.bureau_code || record.bureau || '').toUpperCase();
        if (bureauFilter === 'transunion') return bureau === 'TU' || bureau.includes('TRANSUNION');
        if (bureauFilter === 'experian') return bureau === 'EX' || bureau.includes('EXPERIAN');
        if (bureauFilter === 'equifax') return bureau === 'EQ' || bureau.includes('EQUIFAX');
        return true;
      });
    }
    
    return filtered;
  }, [enrichedRecords, searchQuery, bureauFilter]);
  
  // Selection handlers
  const isAllSelected = filteredRecords.length > 0 && 
    filteredRecords.every(record => selectedIds.has(record.record_id));
  
  const toggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredRecords.map(record => record.record_id)));
    }
  }, [isAllSelected, filteredRecords]);
  
  const toggleSelect = useCallback((recordId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(recordId)) {
        next.delete(recordId);
      } else {
        next.add(recordId);
      }
      return next;
    });
  }, []);
  
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);
  
  // Get selected records
  const selectedRecords = useMemo(() => {
    return filteredRecords.filter(record => selectedIds.has(record.record_id));
  }, [filteredRecords, selectedIds]);
  
  // Handle view details
  const handleViewDetails = useCallback((record) => {
    setSelectedRecord(record);
    setShowDetailModal(true);
  }, []);
  
  // Handle single dispute
  const handleDisputeSingle = useCallback((record) => {
    navigate('/disputes', { 
      state: { 
        publicRecord: record,
        disputeType: 'public_record'
      } 
    });
  }, [navigate]);
  
  // Handle bulk dispute
  const handleBulkDispute = useCallback(() => {
    navigate('/disputes', { 
      state: { 
        publicRecords: selectedRecords,
        disputeType: 'public_record_bulk'
      } 
    });
  }, [selectedRecords, navigate]);
  
  // Format record type for display
  const formatRecordType = (type) => {
    if (!type) return 'Public Record';
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };
  
  return (
    <div className="space-y-4" data-testid="public-records-table">
      {/* Page Title */}
      <div className="mb-2">
        <h3 
          className="text-lg font-bold"
          style={{ color: theme?.text?.primary || '#111827' }}
        >
          Public Records by Bureau
        </h3>
      </div>
      
      {/* Bureau Summary Cards - Always visible */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        {['transunion', 'experian', 'equifax'].map(bureau => {
          const count = actualBureauCounts[bureau] ?? bureauCounts[bureau] ?? 0;
          return (
            <div 
              key={bureau} 
              className="text-center p-4 rounded-xl"
              style={{
                background: count > 0 
                  ? (theme?.tag?.red?.bg || 'rgba(239,68,68,0.12)') 
                  : (theme?.surface?.s2 || 'rgba(255,255,255,0.76)'),
                border: `1px solid ${count > 0 
                  ? 'rgba(239,68,68,0.20)' 
                  : (theme?.border?.b2 || 'rgba(15,172,108,0.12)')}`,
              }}
            >
              <p 
                className="text-2xl font-bold"
                style={{ color: count > 0 ? (theme?.text?.danger || '#EF4444') : (theme?.metric?.value || '#111827') }}
              >
                {count}
              </p>
              <p 
                className="text-sm capitalize"
                style={{ color: theme?.metric?.label || '#6B7280' }}
              >
                {bureau}
              </p>
            </div>
          );
        })}
      </div>
      
      {/* Empty State */}
      {totalRecords === 0 && <PublicRecordsEmptyState theme={theme} />}
      
      {/* Data Present State */}
      {totalRecords > 0 && (
        <>
          {/* Filters Row - Only shown when data exists */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div 
                className="relative flex items-center h-10 px-3 rounded-xl w-64"
                style={{
                  background: theme?.input?.bg || 'rgba(255,255,255,0.72)',
                  border: `1px solid ${theme?.input?.border || 'rgba(15,172,108,0.12)'}`,
                  backdropFilter: 'blur(12px)',
                }}
              >
                <Search size={15} className="flex-shrink-0" style={{ color: theme?.icon?.neutral || '#5F6F64' }} />
                <input
                  type="text"
                  placeholder="Search public records..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 bg-transparent border-none outline-none ml-2.5 text-sm"
                  style={{ color: theme?.input?.text || '#111827' }}
                  data-testid="public-records-search"
                />
              </div>
              
              {/* Bureau Filter */}
              <Select value={bureauFilter} onValueChange={setBureauFilter}>
                <SelectTrigger 
                  className="h-10 px-3 w-36 font-semibold text-sm"
                  style={{
                    background: theme?.input?.bg || 'rgba(255,255,255,0.72)',
                    border: `1px solid ${theme?.input?.border || 'rgba(15,172,108,0.12)'}`,
                    borderRadius: '12px',
                    color: theme?.text?.secondary || '#4B5563',
                    backdropFilter: 'blur(12px)',
                  }}
                  data-testid="public-records-bureau-filter"
                >
                  <Filter size={14} className="mr-2 flex-shrink-0" style={{ color: theme?.icon?.neutral || '#5F6F64' }} />
                  <SelectValue placeholder="All Bureaus" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Bureaus</SelectItem>
                  <SelectItem value="transunion">TransUnion</SelectItem>
                  <SelectItem value="experian">Experian</SelectItem>
                  <SelectItem value="equifax">Equifax</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div 
              className="text-sm"
              style={{ color: theme?.text?.muted || '#6B7280' }}
            >
              {filteredRecords.length === totalRecords 
                ? `${totalRecords} record${totalRecords === 1 ? '' : 's'}`
                : `${filteredRecords.length} of ${totalRecords} record${totalRecords === 1 ? '' : 's'}`
              }
            </div>
          </div>
          
          {/* Note about unclassified records */}
          {actualBureauCounts.unknown > 0 && (
            <div className="text-xs" style={{ color: theme?.text?.muted || '#6B7280' }}>
              Note: {actualBureauCounts.unknown} record{actualBureauCounts.unknown === 1 ? '' : 's'} without bureau classification
            </div>
          )}
          
          {/* Table */}
          {filteredRecords.length > 0 ? (
            <div 
              className="overflow-x-auto rounded-xl"
              style={{
                background: theme?.table?.container || 'rgba(255,255,255,0.84)',
                border: `1px solid ${theme?.border?.b1 || 'rgba(255,255,255,0.44)'}`,
              }}
            >
              <table className="w-full min-w-max">
                <thead>
                  <tr style={{ background: theme?.table?.header || 'rgba(255,255,255,0.60)' }}>
                    <th 
                      className="text-center py-3 px-3 w-12"
                      style={{ borderBottom: `1px solid ${theme?.table?.border || 'rgba(15,172,108,0.10)'}` }}
                    >
                      <Checkbox 
                        checked={isAllSelected}
                        onCheckedChange={toggleSelectAll}
                        className="data-[state=checked]:bg-emerald-600"
                        data-testid="select-all-records"
                      />
                    </th>
                    <th 
                      className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide"
                      style={{ color: theme?.text?.muted, borderBottom: `1px solid ${theme?.table?.border}` }}
                    >
                      Record Type
                    </th>
                    <th 
                      className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide"
                      style={{ color: theme?.text?.muted, borderBottom: `1px solid ${theme?.table?.border}` }}
                    >
                      Filing Date
                    </th>
                    <th 
                      className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide"
                      style={{ color: theme?.text?.muted, borderBottom: `1px solid ${theme?.table?.border}` }}
                    >
                      Court
                    </th>
                    <th 
                      className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide"
                      style={{ color: theme?.text?.muted, borderBottom: `1px solid ${theme?.table?.border}` }}
                    >
                      Reference Number
                    </th>
                    <th 
                      className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide"
                      style={{ color: theme?.text?.muted, borderBottom: `1px solid ${theme?.table?.border}` }}
                    >
                      Status
                    </th>
                    <th 
                      className="text-center py-3 px-4 text-xs font-bold uppercase tracking-wide"
                      style={{ color: theme?.text?.muted, borderBottom: `1px solid ${theme?.table?.border}` }}
                    >
                      Credit Bureau
                    </th>
                    <th 
                      className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide"
                      style={{ color: theme?.text?.muted, borderBottom: `1px solid ${theme?.table?.border}` }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record, idx) => {
                    const isSelected = selectedIds.has(record.record_id);
                    const rowBg = isSelected 
                      ? theme?.table?.rowHover 
                      : idx % 2 === 0 
                        ? theme?.table?.row 
                        : theme?.table?.rowAlt;
                    
                    return (
                      <tr 
                        key={record.record_id}
                        className="transition-colors"
                        style={{
                          background: rowBg,
                          borderBottom: `1px solid ${theme?.table?.border}`,
                        }}
                        data-testid={`record-row-${record.record_id}`}
                      >
                        {/* Select */}
                        <td className="py-4 px-3 text-center">
                          <Checkbox 
                            checked={isSelected}
                            onCheckedChange={() => toggleSelect(record.record_id)}
                            className="data-[state=checked]:bg-emerald-600"
                          />
                        </td>
                        
                        {/* Record Type */}
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                              style={{ background: theme?.tag?.red?.bg || 'rgba(239,68,68,0.12)' }}
                            >
                              <AlertTriangle size={18} style={{ color: theme?.icon?.danger || '#EF4444' }} />
                            </div>
                            <p 
                              className="font-semibold"
                              style={{ color: theme?.text?.primary }}
                            >
                              {formatRecordType(record.type || record.record_type)}
                            </p>
                          </div>
                        </td>
                        
                        {/* Filing Date */}
                        <td className="py-4 px-4">
                          <div 
                            className="flex items-center gap-1 text-sm"
                            style={{ color: theme?.text?.secondary }}
                          >
                            <Calendar size={14} style={{ color: theme?.icon?.neutral }} />
                            {record.filing_date || record.date || '--'}
                          </div>
                        </td>
                        
                        {/* Court */}
                        <td className="py-4 px-4">
                          <span 
                            className="text-sm"
                            style={{ color: theme?.text?.secondary }}
                          >
                            {record.court || '--'}
                          </span>
                        </td>
                        
                        {/* Reference Number */}
                        <td className="py-4 px-4">
                          <span 
                            className="text-sm font-mono"
                            style={{ color: theme?.text?.secondary }}
                          >
                            {record.reference_number || record.case_number || '--'}
                          </span>
                        </td>
                        
                        {/* Status */}
                        <td className="py-4 px-4">
                          <span 
                            className="text-xs px-2 py-1 rounded-full font-semibold capitalize"
                            style={{ 
                              background: (record.status || '').toLowerCase().includes('discharged') 
                                ? theme?.tag?.green?.bg 
                                : theme?.tag?.red?.bg,
                              color: (record.status || '').toLowerCase().includes('discharged') 
                                ? theme?.tag?.green?.color 
                                : theme?.tag?.red?.color,
                            }}
                          >
                            {record.status || '--'}
                          </span>
                        </td>
                        
                        {/* Bureau */}
                        <td className="py-4 px-4 text-center">
                          <span 
                            className="text-xs px-2 py-1 rounded-full font-semibold"
                            style={{ 
                              background: theme?.bureauPill?.bg,
                              border: `1px solid ${theme?.bureauPill?.border}`,
                              color: theme?.bureauPill?.color,
                            }}
                          >
                            {record.bureau_code || record.bureau || '--'}
                          </span>
                        </td>
                        
                        {/* Actions */}
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewDetails(record)}
                              data-testid={`view-record-${record.record_id}`}
                              style={{ color: theme?.icon?.neutral }}
                            >
                              <Eye size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDisputeSingle(record)}
                              data-testid={`dispute-record-${record.record_id}`}
                              style={{ color: theme?.icon?.primary }}
                            >
                              <Plus size={16} />
                              <span className="ml-1">Dispute</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div 
              className="text-center py-8"
              style={{ color: theme?.text?.muted }}
            >
              No public records match your search criteria.
            </div>
          )}
          
          {/* Bulk Action Bar */}
          <BulkActionBar
            selectedCount={selectedIds.size}
            onDispute={handleBulkDispute}
            onClear={clearSelection}
          />
        </>
      )}
      
      {/* Detail Modal */}
      <RecordDetailModal
        record={selectedRecord}
        open={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedRecord(null);
        }}
      />
    </div>
  );
};

export default PublicRecordsTable;
